import { useState, useEffect } from 'react';
import { milestoneController } from '../../controllers/milestoneController';
import { projectController } from '../../controllers/projectController';
import { 
  Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle, 
  ChevronUp, ChevronDown, Flag, CheckCircle, Clock, AlertTriangle,
  Calendar, TrendingUp, Target, ListChecks
} from 'lucide-react';

const MilestonesPage = () => {
  const [milestones, setMilestones] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filteredMilestones, setFilteredMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterProject, setFilterProject] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    projectId: '',
    dueDate: '',
    status: 'Pending',
    completionPercentage: 0,
    deliverables: []
  });
  const [newDeliverable, setNewDeliverable] = useState('');
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [sortField, setSortField] = useState('dueDate');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMilestones, setSelectedMilestones] = useState([]);
  const itemsPerPage = 10;

  useEffect(() => { 
    fetchMilestones(); 
    fetchProjects();
  }, []);

  useEffect(() => {
    let result = [...milestones];
    
    if (searchQuery) {
      result = result.filter(m =>
        (m.name || m.milestoneName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (m.milestoneId || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (m.description || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (filterStatus !== 'All') {
      result = result.filter(m => m.status === filterStatus);
    }
    
    if (filterProject !== 'All') {
      result = result.filter(m => {
        const projectId = m.projectId?._id || m.projectId;
        return projectId === filterProject;
      });
    }
    
    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    setFilteredMilestones(result);
    setCurrentPage(1);
  }, [milestones, searchQuery, filterStatus, filterProject, sortField, sortDirection]);

  const fetchMilestones = async () => {
    setLoading(true);
    const result = await milestoneController.getAll();
    if (result.success) {
      setMilestones(result.data);
    } else {
      alert('Error: ' + result.message);
    }
    setLoading(false);
  };

  const fetchProjects = async () => {
    const result = await projectController.getAll();
    if (result.success) {
      setProjects(result.data);
    }
  };

  const getProjectName = (projectId) => {
    if (typeof projectId === 'object' && projectId !== null) {
      return projectId.name || projectId.projectName || 'Unknown Project';
    }
    const project = projects.find(p => (p.id || p._id) === projectId);
    return project ? (project.name || project.projectName) : 'Unknown Project';
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': '#f59e0b',
      'In Progress': '#3b82f6',
      'Completed': '#10b981',
      'Delayed': '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  const isOverdue = (milestone) => {
    if (!milestone.dueDate || milestone.status === 'Completed') return false;
    return new Date(milestone.dueDate) < new Date();
  };

  const getDaysUntilDue = (milestone) => {
    if (!milestone.dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(milestone.dueDate);
    due.setHours(0, 0, 0, 0);
    return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors([]);
  };

  const handleAddDeliverable = () => {
    if (newDeliverable.trim()) {
      setFormData(prev => ({
        ...prev,
        deliverables: [...prev.deliverables, newDeliverable.trim()]
      }));
      setNewDeliverable('');
    }
  };

  const handleRemoveDeliverable = (index) => {
    setFormData(prev => ({
      ...prev,
      deliverables: prev.deliverables.filter((_, i) => i !== index)
    }));
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      description: '',
      projectId: '',
      dueDate: '',
      status: 'Pending',
      completionPercentage: 0,
      deliverables: []
    });
    setNewDeliverable('');
    setFormErrors([]);
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    
    const result = await milestoneController.create(formData);
    if (result.success) {
      setShowAddModal(false);
      fetchMilestones();
      alert('✅ Milestone created successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    setFormSubmitting(false);
  };

  const handleEdit = (milestone) => {
    setSelectedMilestone(milestone);
    
    let projectIdValue = '';
    if (milestone.projectId) {
      if (typeof milestone.projectId === 'object' && (milestone.projectId.id || milestone.projectId._id)) {
        projectIdValue = milestone.projectId.id || milestone.projectId._id;
      } else if (typeof milestone.projectId === 'string') {
        projectIdValue = milestone.projectId;
      }
    }
    
    // Extract deliverable names from objects if needed
    let deliverablesList = [];
    if (milestone.deliverables && Array.isArray(milestone.deliverables)) {
      deliverablesList = milestone.deliverables.map(d => {
        if (typeof d === 'object' && d.name) {
          return d.name;
        }
        return d;
      });
    }
    
    setFormData({
      name: milestone.name || milestone.milestoneName || '',
      description: milestone.description || '',
      projectId: projectIdValue,
      dueDate: milestone.dueDate?.split('T')[0] || '',
      status: milestone.status || 'Pending',
      completionPercentage: milestone.completionPercentage || milestone.progress || 0,
      deliverables: deliverablesList
    });
    setNewDeliverable('');
    setFormErrors([]);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    
    const result = await milestoneController.update(selectedMilestone.id || selectedMilestone._id, formData);
    if (result.success) {
      setShowEditModal(false);
      fetchMilestones();
      alert('✅ Milestone updated successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    setFormSubmitting(false);
  };

  const handleView = (milestone) => {
    setSelectedMilestone(milestone);
    setShowViewModal(true);
  };

  const handleDelete = async (milestone) => {
    try {
      const isConfirmed = window.confirm(
        `Delete milestone "${milestone.name || milestone.milestoneName}"?`
      );
  
      if (!isConfirmed) return;
  
      const result = await milestoneController.delete(milestone.id || milestone._id);
  
      if (result?.success) {
        await fetchMilestones();
        alert('✅ Milestone deleted successfully!');
      } else {
        alert(result?.message || '❌ Failed to delete milestone');
      }
    } catch (error) {
      console.error('Delete milestone error:', error);
      alert('❌ Something went wrong while deleting milestone');
    }
  };

  const handleStatusChange = async (milestone, newStatus) => {
    const result = await milestoneController.updateStatus(milestone.id || milestone._id, newStatus);
    if (result.success) {
      fetchMilestones();
      alert('✅ Status updated!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = async () => {
    const filters = {};
    if (filterStatus !== 'All') filters.status = filterStatus;
    if (filterProject !== 'All') filters.projectId = filterProject;
    
    const result = await milestoneController.export(filters, 'csv');
    if (result.success) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `milestones-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert('✅ Milestones exported successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleSelectMilestone = (milestoneId) => {
    setSelectedMilestones(prev => 
      prev.includes(milestoneId) 
        ? prev.filter(id => id !== milestoneId)
        : [...prev, milestoneId]
    );
  };

  const handleSelectAll = () => {
    if (selectedMilestones.length === currentMilestones.length && currentMilestones.length > 0) {
      setSelectedMilestones([]);
    } else {
      setSelectedMilestones(currentMilestones.map(m => m.id || m._id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMilestones.length === 0) {
      alert('Please select milestones to delete');
      return;
    }
    
    const confirmed = window.confirm(`Delete ${selectedMilestones.length} selected milestones?`);
    if (!confirmed) return;
    
    const result = await milestoneController.bulkDelete(selectedMilestones);
    if (result.success) {
      setSelectedMilestones([]);
      fetchMilestones();
      alert('✅ Milestones deleted successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedMilestones.length === 0) {
      alert('Please select milestones to update');
      return;
    }
    
    const result = await milestoneController.bulkUpdate(selectedMilestones, { status: newStatus });
    if (result.success) {
      setSelectedMilestones([]);
      fetchMilestones();
      alert('✅ Milestones updated successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentMilestones = filteredMilestones.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredMilestones.length / itemsPerPage);

  const stats = {
    total: milestones.length,
    pending: milestones.filter(m => m.status === 'Pending').length,
    inProgress: milestones.filter(m => m.status === 'In Progress').length,
    completed: milestones.filter(m => m.status === 'Completed').length,
    overdue: milestones.filter(m => isOverdue(m)).length
  };

  const styles = {
    container: { padding: 0 },
    header: { 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      marginBottom: '24px', 
      flexWrap: 'wrap', 
      gap: '16px' 
    },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 },
    button: { 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px', 
      padding: '10px 20px', 
      backgroundColor: '#3b82f6', 
      color: '#fff', 
      border: 'none', 
      borderRadius: '8px', 
      fontSize: '14px', 
      fontWeight: '500', 
      cursor: 'pointer' 
    },
    secondaryButton: { 
      backgroundColor: '#fff', 
      color: '#1e293b', 
      border: '1px solid #e2e8f0' 
    },
    dangerButton: {
      backgroundColor: '#ef4444',
      color: '#fff'
    },
    statsBar: { 
      display: 'grid', 
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
      gap: '16px', 
      marginBottom: '24px' 
    },
    statCard: { 
      backgroundColor: '#fff', 
      padding: '20px', 
      borderRadius: '12px', 
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
    },
    statLabel: { fontSize: '14px', color: '#64748b', marginBottom: '8px' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b' },
    filterBar: { 
      display: 'flex', 
      gap: '12px', 
      marginBottom: '20px', 
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    searchBox: { flex: 1, minWidth: '250px', position: 'relative' },
    searchInput: { 
      width: '100%', 
      padding: '10px 10px 10px 40px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none' 
    },
    searchIcon: { 
      position: 'absolute', 
      left: '12px', 
      top: '50%', 
      transform: 'translateY(-50%)' 
    },
    select: { 
      padding: '10px 16px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none', 
      backgroundColor: '#fff', 
      cursor: 'pointer' 
    },
    card: { 
      backgroundColor: '#fff', 
      borderRadius: '12px', 
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
      overflow: 'hidden' 
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { 
      backgroundColor: '#f8fafc', 
      padding: '16px', 
      textAlign: 'left', 
      fontSize: '13px', 
      fontWeight: '600', 
      color: '#475569', 
      borderBottom: '1px solid #e2e8f0', 
      cursor: 'pointer', 
      userSelect: 'none' 
    },
    td: { 
      padding: '16px', 
      borderBottom: '1px solid #e2e8f0', 
      fontSize: '14px', 
      color: '#1e293b' 
    },
    badge: { 
      display: 'inline-block', 
      padding: '4px 12px', 
      borderRadius: '12px', 
      fontSize: '12px', 
      fontWeight: '500' 
    },
    actionButton: { 
      padding: '6px', 
      backgroundColor: 'transparent', 
      border: 'none', 
      borderRadius: '6px', 
      cursor: 'pointer' 
    },
    pagination: { 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      padding: '20px', 
      borderTop: '1px solid #e2e8f0' 
    },
    modal: { 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000, 
      padding: '20px' 
    },
    modalContent: { 
      backgroundColor: '#fff', 
      borderRadius: '12px', 
      width: '100%', 
      maxWidth: '800px', 
      maxHeight: '90vh', 
      overflow: 'auto' 
    },
    modalHeader: { 
      padding: '20px 24px', 
      borderBottom: '1px solid #e2e8f0', 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center' 
    },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 },
    modalBody: { padding: '24px' },
    formGroup: { marginBottom: '20px' },
    label: { 
      display: 'block', 
      marginBottom: '8px', 
      fontSize: '14px', 
      fontWeight: '500', 
      color: '#374151' 
    },
    input: { 
      width: '100%', 
      padding: '10px 12px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none' 
    },
    textarea: { 
      width: '100%', 
      padding: '10px 12px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none', 
      minHeight: '100px', 
      resize: 'vertical' 
    },
    errorList: { 
      backgroundColor: '#fee2e2', 
      border: '1px solid #fecaca', 
      borderRadius: '8px', 
      padding: '12px 16px', 
      marginBottom: '20px' 
    },
    errorItem: { 
      color: '#dc2626', 
      fontSize: '14px', 
      marginBottom: '4px', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px' 
    },
    modalFooter: { 
      padding: '16px 24px', 
      borderTop: '1px solid #e2e8f0', 
      display: 'flex', 
      justifyContent: 'flex-end', 
      gap: '12px' 
    },
    progressBar: { 
      width: '100%', 
      height: '8px', 
      backgroundColor: '#e2e8f0', 
      borderRadius: '4px', 
      overflow: 'hidden' 
    },
    progressFill: { 
      height: '100%', 
      backgroundColor: '#3b82f6', 
      transition: 'width 0.3s' 
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer'
    },
    deliverablesList: {
      marginTop: '12px'
    },
    deliverableItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 12px',
      backgroundColor: '#f8fafc',
      borderRadius: '6px',
      marginBottom: '8px',
      fontSize: '14px'
    },
    deliverableInput: {
      display: 'flex',
      gap: '8px',
      marginTop: '8px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Project Milestones</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {selectedMilestones.length > 0 && (
            <>
              <button 
                style={{ ...styles.button, ...styles.dangerButton }} 
                onClick={handleBulkDelete}
              >
                <Trash2 size={18} /> Delete ({selectedMilestones.length})
              </button>
              <select 
                style={styles.select}
                onChange={(e) => {
                  if (e.target.value) {
                    handleBulkStatusUpdate(e.target.value);
                    e.target.value = '';
                  }
                }}
              >
                <option value="">Update Status</option>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Delayed">Delayed</option>
              </select>
            </>
          )}
          <button 
            style={{ ...styles.button, ...styles.secondaryButton }} 
            onClick={handleExport}
          >
            <Download size={18} /> Export
          </button>
          <button style={styles.button} onClick={handleAdd}>
            <Plus size={18} /> Add Milestone
          </button>
        </div>
      </div>

      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Milestones</div>
            <Target size={24} color="#3b82f6" />
          </div>
          <div style={styles.statValue}>{stats.total}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>In Progress</div>
            <TrendingUp size={24} color="#3b82f6" />
          </div>
          <div style={{ ...styles.statValue, color: '#3b82f6' }}>{stats.inProgress}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Completed</div>
            <CheckCircle size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.completed}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Overdue</div>
            <AlertTriangle size={24} color="#ef4444" />
          </div>
          <div style={{ ...styles.statValue, color: '#ef4444' }}>{stats.overdue}</div>
        </div>
      </div>

      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input 
            type="text" 
            placeholder="Search milestones..." 
            style={styles.searchInput} 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
          />
        </div>
        <select 
          style={styles.select} 
          value={filterProject} 
          onChange={(e) => setFilterProject(e.target.value)}
        >
          <option value="All">All Projects</option>
          {projects.map(project => (
            <option key={project.id || project._id} value={project.id || project._id}>
              {project.name || project.projectName}
            </option>
          ))}
        </select>
        <select 
          style={styles.select} 
          value={filterStatus} 
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="All">All Status</option>
          <option value="Pending">Pending</option>
          <option value="In Progress">In Progress</option>
          <option value="Completed">Completed</option>
          <option value="Delayed">Delayed</option>
        </select>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            Loading milestones...
          </div>
        ) : currentMilestones.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            No milestones found. Click "Add Milestone" to create one.
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: '50px' }}>
                    <input 
                      type="checkbox"
                      style={styles.checkbox}
                      checked={selectedMilestones.length === currentMilestones.length && currentMilestones.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th style={styles.th} onClick={() => handleSort('milestoneId')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      ID {sortField === 'milestoneId' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th} onClick={() => handleSort('name')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Milestone Name {sortField === 'name' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Project</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Due Date</th>
                  <th style={styles.th}>Progress</th>
                  <th style={styles.th}>Deliverables</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentMilestones.map((milestone) => {
                  const milestoneOverdue = isOverdue(milestone);
                  const daysUntilDue = getDaysUntilDue(milestone);
                  
                  return (
                    <tr key={milestone.id || milestone._id} style={{ cursor: 'pointer' }}>
                      <td style={styles.td}>
                        <input 
                          type="checkbox"
                          style={styles.checkbox}
                          checked={selectedMilestones.includes(milestone.id || milestone._id)}
                          onChange={() => handleSelectMilestone(milestone.id || milestone._id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                          {milestone.milestoneId}
                        </div>
                      </td>
                      <td style={styles.td} onClick={() => handleView(milestone)}>
                        <div style={{ fontWeight: '500' }}>{milestone.name || milestone.milestoneName}</div>
                        {milestone.description && (
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                            {milestone.description.substring(0, 60)}{milestone.description.length > 60 ? '...' : ''}
                          </div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontSize: '13px' }}>
                          {getProjectName(milestone.projectId)}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <select
                          style={{
                            ...styles.badge,
                            backgroundColor: `${getStatusColor(milestone.status)}20`,
                            color: getStatusColor(milestone.status),
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          value={milestone.status}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleStatusChange(milestone, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="Pending">Pending</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                          <option value="Delayed">Delayed</option>
                        </select>
                      </td>
                      <td style={styles.td}>
                        <div style={{ 
                          fontWeight: '500',
                          color: milestoneOverdue ? '#ef4444' : '#1e293b'
                        }}>
                          {milestone.dueDate ? new Date(milestone.dueDate).toLocaleDateString() : 'No due date'}
                        </div>
                        {milestone.dueDate && !milestoneOverdue && daysUntilDue >= 0 && (
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            {daysUntilDue === 0 ? 'Due today' : `${daysUntilDue} days left`}
                          </div>
                        )}
                        {milestoneOverdue && (
                          <div style={{ fontSize: '12px', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <AlertTriangle size={12} /> Overdue
                          </div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <div style={{ marginBottom: '4px', fontSize: '12px', color: '#64748b' }}>
                          {milestone.completionPercentage || milestone.progress || 0}%
                        </div>
                        <div style={styles.progressBar}>
                          <div style={{ ...styles.progressFill, width: `${milestone.completionPercentage || milestone.progress || 0}%` }} />
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <ListChecks size={16} color="#64748b" />
                          <span style={{ fontSize: '13px', color: '#64748b' }}>
                            {Array.isArray(milestone.deliverables) ? milestone.deliverables.length : 0}
                          </span>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleView(milestone); }} 
                            title="View"
                          >
                            <Eye size={18} color="#64748b" />
                          </button>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleEdit(milestone); }} 
                            title="Edit"
                          >
                            <Edit2 size={18} color="#3b82f6" />
                          </button>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleDelete(milestone); }} 
                            title="Delete"
                          >
                            <Trash2 size={18} color="#ef4444" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredMilestones.length)} of {filteredMilestones.length} milestones
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '14px', color: '#64748b' }}>
                  Page {currentPage} of {totalPages}
                </div>
                <button 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {showAddModal ? 'Add New Milestone' : 'Edit Milestone'}
              </h2>
              <button 
                style={{ ...styles.actionButton, padding: '8px' }} 
                onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (
                  <div style={styles.errorList}>
                    {formErrors.map((error, index) => (
                      <div key={index} style={styles.errorItem}>
                        <AlertCircle size={14} />
                        {error}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Milestone Name *</label>
                    <input 
                      type="text" 
                      name="name" 
                      style={styles.input} 
                      value={formData.name} 
                      onChange={handleInputChange} 
                      required 
                      placeholder="Enter milestone name"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Project *</label>
                    <select 
                      name="projectId" 
                      style={styles.input} 
                      value={formData.projectId} 
                      onChange={handleInputChange} 
                      required
                    >
                      <option value="">Select Project</option>
                      {projects.map(project => (
                        <option key={project.id || project._id} value={project.id || project._id}>
                          {project.name || project.projectName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <select 
                      name="status" 
                      style={styles.input} 
                      value={formData.status} 
                      onChange={handleInputChange}
                    >
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Delayed">Delayed</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Due Date *</label>
                    <input 
                      type="date" 
                      name="dueDate" 
                      style={styles.input} 
                      value={formData.dueDate} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Progress (%)</label>
                    <input 
                      type="number" 
                      name="completionPercentage" 
                      style={styles.input} 
                      value={formData.completionPercentage} 
                      onChange={handleInputChange} 
                      min="0" 
                      max="100" 
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Description</label>
                  <textarea 
                    name="description" 
                    style={styles.textarea} 
                    value={formData.description} 
                    onChange={handleInputChange}
                    placeholder="Enter milestone description"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Deliverables</label>
                  <div style={styles.deliverableInput}>
                    <input 
                      type="text" 
                      style={{ ...styles.input, flex: 1 }} 
                      value={newDeliverable}
                      onChange={(e) => setNewDeliverable(e.target.value)}
                      placeholder="Add deliverable"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddDeliverable();
                        }
                      }}
                    />
                    <button 
                      type="button"
                      style={styles.button}
                      onClick={handleAddDeliverable}
                    >
                      <Plus size={18} /> Add
                    </button>
                  </div>
                  {formData.deliverables.length > 0 && (
                    <div style={styles.deliverablesList}>
                      {formData.deliverables.map((deliverable, index) => (
                        <div key={index} style={styles.deliverableItem}>
                          <span>{deliverable}</span>
                          <button 
                            type="button"
                            style={styles.actionButton}
                            onClick={() => handleRemoveDeliverable(index)}
                          >
                            <X size={16} color="#ef4444" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button 
                  type="button" 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => { setShowAddModal(false); setShowEditModal(false); }} 
                  disabled={formSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>
                  {formSubmitting ? 'Saving...' : (showAddModal ? 'Create Milestone' : 'Update Milestone')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedMilestone && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Milestone Details</h2>
              <button 
                style={{ ...styles.actionButton, padding: '8px' }} 
                onClick={() => setShowViewModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Milestone ID</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedMilestone.milestoneId}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Milestone Name</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedMilestone.name || selectedMilestone.milestoneName}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Project</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {getProjectName(selectedMilestone.projectId)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div>
                  <span style={{ 
                    ...styles.badge, 
                    backgroundColor: `${getStatusColor(selectedMilestone.status)}20`, 
                    color: getStatusColor(selectedMilestone.status) 
                  }}>
                    {selectedMilestone.status}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Due Date</div>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: '500', 
                    color: isOverdue(selectedMilestone) ? '#ef4444' : '#1e293b' 
                  }}>
                    {selectedMilestone.dueDate ? new Date(selectedMilestone.dueDate).toLocaleDateString() : 'N/A'}
                  </div>
                  {isOverdue(selectedMilestone) && (
                    <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                      Overdue by {Math.abs(getDaysUntilDue(selectedMilestone))} days
                    </div>
                  )}
                </div>

                {selectedMilestone.completedDate && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Completed Date</div>
                    <div style={{ fontSize: '16px', fontWeight: '500', color: '#10b981' }}>
                      {new Date(selectedMilestone.completedDate).toLocaleDateString()}
                    </div>
                  </div>
                )}

                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Progress</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b', marginBottom: '8px' }}>
                    {selectedMilestone.completionPercentage || selectedMilestone.progress || 0}%
                  </div>
                  <div style={styles.progressBar}>
                    <div style={{ ...styles.progressFill, width: `${selectedMilestone.completionPercentage || selectedMilestone.progress || 0}%` }} />
                  </div>
                </div>
              </div>

              {selectedMilestone.description && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Description</div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#1e293b', 
                    padding: '12px', 
                    backgroundColor: '#f8fafc', 
                    borderRadius: '8px' 
                  }}>
                    {selectedMilestone.description}
                  </div>
                </div>
              )}

              {selectedMilestone.deliverables && selectedMilestone.deliverables.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>
                    Deliverables ({selectedMilestone.deliverables.length})
                  </div>
                  <div style={styles.deliverablesList}>
                    {selectedMilestone.deliverables.map((deliverable, index) => {
                      const deliverableName = typeof deliverable === 'object' ? deliverable.name : deliverable;
                      const isCompleted = typeof deliverable === 'object' ? deliverable.isCompleted : false;
                      
                      return (
                        <div key={index} style={styles.deliverableItem}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <CheckCircle size={16} color={isCompleted ? "#10b981" : "#64748b"} />
                            <span style={{ textDecoration: isCompleted ? 'line-through' : 'none' }}>
                              {deliverableName}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button 
                style={{ ...styles.button, ...styles.secondaryButton }} 
                onClick={() => { setShowViewModal(false); handleEdit(selectedMilestone); }}
              >
                <Edit2 size={16} /> Edit Milestone
              </button>
              <button style={styles.button} onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MilestonesPage;